import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from '../services/auth/token.service';
import { AuthService } from '../services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokenService = inject(TokenService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // No agregar token a las rutas de auth
    if (this.isAuthRequest(req)) {
      return next.handle(req);
    }

    const token = this.tokenService.getAccessToken();

    // Si no hay token, simplemente continuar sin él (probablemente sea contenido público)
    if (!token) {
      return next.handle(req);
    }

    // Agregar token a la petición
    req = this.addTokenToRequest(req, token);

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isAuthRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('/auth/login') ||
      request.url.includes('/auth/refresh') ||
      request.url.includes('/auth/logout')
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Verificar si tenemos un refresh token válido
      if (!this.tokenService.hasValidRefreshToken()) {
        // No hay refresh token válido, cerrar sesión
        this.isRefreshing = false;
        this.logout();
        return EMPTY;
      }

      // Intentar renovar el token
      return this.authService.refreshTokens().pipe(
        switchMap((response) => {
          this.isRefreshing = false;
          const accessToken = response.data.tokens.accessToken;
          this.refreshTokenSubject.next(accessToken);

          // Reintentar la petición original con el nuevo token
          return next.handle(this.addTokenToRequest(request, accessToken));
        }),
        catchError((error) => {
          // Si falla el refresh token, cerrar sesión
          this.isRefreshing = false;
          this.logout();
          return throwError(() => error);
        }),
      );
    } else {
      // Si ya se está refrescando, esperar a que termine
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenToRequest(request, token));
        }),
      );
    }
  }

  private logout(): void {
    this.authService.logout();

    // Usar setTimeout para evitar problemas con el router en el contexto del interceptor
    setTimeout(() => {
      this.router.navigate(['/login'], {
        queryParams: { reason: 'session_expired' },
      });
    }, 0);
  }
}
